import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import {
  TTransaction,
  transactionsAtom,
} from 'structure/bridge/store/transactionsAtom';
import {
  useReadContract,
  useWriteContract,
  useSimulateContract,
  useBlockNumber,
} from 'wagmi';
import PROXY_OFTV2_ABI from '../../constants/abis/ProxyOFTV2WithFee';
import { MessageStatus } from 'structure/bridge/constants/messageStatus';
import { useQueryClient } from '@tanstack/react-query';
import { ETH_AVERAGE_BLOCK_TIME } from 'utils/constants/ethConst';

interface Props {
  transaction: TTransaction;
}

export function BridgeFailedTransactionRetryButton({ transaction }: Props) {
  const toastV2 = useToast();
  const isMobile = useIsMobile();
  const [transactions, setTransactions] = useAtom(transactionsAtom);

  // Get oft contract outboundAmount if we have a proxy oft contract
  const { data: outboundAmount = BigInt(0), queryKey: outboundAmountQueryKey } =
    useReadContract({
      query: {
        enabled: !!transaction.token?.oftAddress[transaction.dstChainId],
      },
      abi: PROXY_OFTV2_ABI,
      functionName: 'outboundAmount',
      chainId: transaction.dstChainId,
      address: transaction.token.oftAddress[transaction.dstChainId],
    });

  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({
    watch: true,
    cacheTime: ETH_AVERAGE_BLOCK_TIME * 5,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: outboundAmountQueryKey });
  }, [blockNumber, queryClient]);

  // Retry transaction
  const retryMessageSimulation = useSimulateContract({
    query: {
      enabled:
        outboundAmount >= BigInt(transaction.amount) &&
        !!transaction.token?.oftAddress[transaction.dstChainId],
    },
    chainId: transaction.dstChainId,
    address: transaction.token.oftAddress[transaction.dstChainId],
    abi: PROXY_OFTV2_ABI,
    functionName: 'retryMessage',
    args: [
      Number(transaction.failedMessage?._srcChainId ?? 0),
      (transaction.failedMessage?._srcAddress ?? '0x') as `0x${string}`,
      BigInt(transaction.failedMessage?._nonce ?? 0),
      (transaction.failedMessage?._payload ?? '0x') as `0x${string}`,
    ],
  });

  const { writeContractAsync: retry } = useWriteContract();

  const handleRetry = async () => {
    await retry?.(
      retryMessageSimulation.data!.request as Parameters<typeof retry>[0]
    );
    const updatedTransactions = transactions.map((tx) => {
      if (tx.srcTxHash === transaction.srcTxHash) {
        return {
          ...tx,
          status: MessageStatus.DELIVERED,
          failedMessage: undefined,
        };
      }
      return tx;
    });
    setTransactions(updatedTransactions);
    toastV2({
      title: 'Retry Successful',
      type: 'success',
      description: `Transaction ${transaction.dstTxHash} has been retried.`,
    });
  };
  return (
    <button
      title={
        outboundAmount < BigInt(transaction.amount)
          ? 'Not enough liquidity in bridge'
          : 'Retry transaction'
      }
      // className={`border-1 flex items-center justify-center rounded-xl border ${isMobile ? 'px-2 py-0' : 'px-3 py-1'} text-white`}
      className={`border-1 whitespace-nowrap rounded-xl border ${isMobile ? 'px-2 py-0 text-[8px]' : 'px-3 py-1'}
                 text-white ${outboundAmount < BigInt(transaction.amount) && 'cursor-not-allowed'}`}
      onClick={handleRetry}
      disabled={outboundAmount < BigInt(transaction.amount)}
    >
      {isMobile ? 'Retry' : 'Try again'}
    </button>
  );
}
